import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { IActivityData } from 'types/order/activities/IActivityData';
import { IActivityQuery } from 'types/order/activities/IActivityQuery';
import { IUserData } from 'types/users/IUser';
import { DataGrid, SmallBodyText } from 'ui-toolkit';
import { IPageData } from 'types/rest/IPageData';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { ActivityDateHeader } from './ActivityDateHeader';
import { CheckboxList } from './CheckboxList';
import { TextTableHeader } from './TextTableHeader';
import { TypeAheadMultiselectHeader } from './TypeAheadMultiselectHeader';

export interface IActivityTableProps {
  queryData: IPageData<IActivityData>;
  queryFetchStatus: IFetchStatus;
  queryObject: IActivityQuery;
  setQueryObject: (x: IActivityQuery) => void;
  actionOptions: string[];
  userOptions: IUserData[];
}

export const ActivityTable: React.FC<IActivityTableProps> = ({
  queryData,
  queryFetchStatus,
  queryObject,
  setQueryObject,
  userOptions,
  actionOptions,
}) => {
  const checkboxListOptions = useMemo(() => actionOptions.map((t) => ({ label: t, value: t })), [actionOptions]);
  return (
    <DataGrid
      columns={[
        {
          dataField: 'action',
          headerStyle: { width: 120 },
          text: 'Action Taken',
          formatter: (column: any) => <SmallBodyText>{column}</SmallBodyText>,
          headerFormatter: () => (
            <CheckboxList
              text="Action Taken"
              values={queryObject.actions}
              options={checkboxListOptions}
              onFilterChange={(actions) => setQueryObject({ ...queryObject, actions })}
            />
          ),
        },
        {
          dataField: 'message',
          headerStyle: { width: 120 },
          text: 'Details',
          formatter: (column: any) => <SmallBodyText dangerouslySetInnerHTML={{ __html: column }}></SmallBodyText>,
          headerFormatter: () => (
            <TextTableHeader
              text={'Details'}
              filter={queryObject.detail}
              onFilterChange={(detail) => setQueryObject({ ...queryObject, detail })}
            />
          ),
        },
        {
          dataField: 'identityName',
          headerStyle: { width: 120 },
          text: 'Teammate',
          headerFormatter: () => (
            <TypeAheadMultiselectHeader
              text={'Teammate'}
              values={queryObject.initiatorIds || []}
              options={userOptions.map((t) => ({ label: t.name!, value: t.id }))}
              onFilterChange={(initiatorIds) => setQueryObject({ ...queryObject, initiatorIds })}
            />
          ),
        },
        {
          dataField: 'occurredAt',
          headerStyle: { width: 120 },
          text: 'Activity Date',
          formatter: (column: any) => (
            <SmallBodyText>
              {DateTime.fromISO(column).weekdayLong},{' '}
              {DateTime.fromISO(column).toLocaleString(DateTime.DATETIME_FULL as any)}
            </SmallBodyText>
          ),
          headerFormatter: () => (
            <ActivityDateHeader
              occurredAtEnd={queryObject.occurredAtEnd}
              occurredAtStart={queryObject.occurredAtStart}
              onFilterChange={({ occurredAtEnd, occurredAtStart, sortBy }) =>
                setQueryObject({ ...queryObject, occurredAtEnd, occurredAtStart, sortBy })
              }
              sortBy={queryObject.sortBy}
            />
          ),
        },
      ]}
      keyField={'id'}
      data={queryData.content}
      loading={queryFetchStatus.fetching}
      remote
      onTableChange={(type, newState) => {}}
    />
  );
};
